import React, { useState, useEffect } from "react";

import { Link, useHistory, useLocation } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";

import Form from "../../components/Forms/Form";
import OrganizationSchemas from "../../schemas/organization.schema";
import { SuccessAlert, ErrorAlert } from "../../utils/Alerts";
import OrganizationsAPI from "../../API/organizations.api";
import { FIELD_TYPES, ROLES } from "../../utils/constants";
import { getRoles } from "../../utils/OktaHelpers";
import { isAuthorized } from "../../utils/RolesHelpers";
import { getCurrentUserOrganization } from "../../utils/ApiHelpers";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { shortDateYMD } from "../../utils/DateHelper";
import BreadCrumb from "../../components/Navbars/BreadCrumb";
import LookupsAPI from "../../API/lookups.api";
import { validate } from "../../utils/ValidationHelpers";
import { getCountriesDropdown } from "../../utils/CountryHelper";
import { getCurrencyDropdown } from "../../utils/CurrencyHelper";
import { formResetHandler, removeUnchangedProps } from "../../utils/Helpers";
import OrganizationPreferencesForm from "../../components/OrganizationProfile/OrganizationPreferencesForm";

const OrganizationForm = () => {
    const [error, setError] = useState({});
    const [isFormValid, setIsFormValid] = useState(false);
    const [organization, setOrganization] = useState(null);
    const [partnerOrganization, setPartnerOrganization] = useState(null);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [organizationId, setOrganizationId] = useState("");
    const [roles, setRoles] = useState([]);
    const [input, setInput] = useState({
        defaultCurrency: null,
        isDemo: false,
    });
    const [labstoreAccesses, setLabstoreAccesses] = useState([]);
    const [countries, setCountries] = useState([]);
    const [currencies, setCurrencies] = useState([]);

    const history = useHistory();
    const { t } = useTranslation();

    const location = useLocation();
    const data = location?.state?.data;

    useEffect(() => {
        const currentRoles = getRoles();
        getCountriesDropdown(setCountries, true);
        getCurrencyDropdown(setCurrencies);
        setRoles(currentRoles);
        if (isAuthorized([ROLES.superAdmin], currentRoles)) {
            getLabStoreAccessesOptions();
        }
        if (
            isAuthorized([ROLES.partnerAdmin, ROLES.support], currentRoles) &&
            !location.state
        ) {
            getCurrentUserOrganization(setPartnerOrganization).then((org) => {
                setInput({ supportedCountryId: org.supportedCountryId });
            });
        }
        if (data) {
            setOrganization(data);
            setInput(mapInput(data));
        }
    }, []);

    useEffect(() => {
        if (currencies) {
            if (!data)
                setInput({
                    ...input,
                    defaultCurrency: currencies[0],
                });
            else {
                const defaultCurrencyId = data?.defaultCurrencyId;
                setInput((prev) => ({
                    ...prev,
                    defaultCurrency: defaultCurrencyId
                        ? currencies.find(
                              (elem) => elem.value === defaultCurrencyId
                          )
                        : currencies[0],
                }));
            }
        }
    }, [currencies]);

    useEffect(() => {
        validate(
            input,
            organization
                ? OrganizationSchemas.EditOrganizationSchema
                : OrganizationSchemas.AddOrganizationSchema,
            setIsFormValid,
            setError,
            {
                context: {
                    isSuperAdmin: isAuthorized([ROLES.superAdmin], roles),
                    isSuperAdminAndParent:
                        isAuthorized([ROLES.superAdmin], getRoles()) &&
                        !data?.partnerId,
                    isCountryRequired:
                        isAuthorized([ROLES.superAdmin], roles) &&
                        (!data || !data.partnerId),
                },
            }
        );
    }, [input]);

    const mapInput = (data) => {
        setOrganizationId(data.id);
        return {
            name: data.name,
            maxUsersNumber: data.maxUsersNumber,
            currentActiveUsers: data.currentActiveUsers,
            type: data.type,
            image: data.image,
            defaultFirmwareVersion: data.defaultFirmwareVersion,
            abbreviation: data.abbreviation || "",
            supportedCountryId: data.supportedCountryId,
            endOfLicense: data.endOfLicense
                ? new Date(data.endOfLicense).toISOString().slice(0, 10)
                : null,
            uploadModelAccess: data?.uploadModelAccess?.toString(),
            externalId: data.externalId,
            createBundleAccess: data?.createBundleAccess?.toString(),
            createCouponAccess: data?.createCouponAccess?.toString(),
            suppliersAccess: data?.suppliersAccess?.toString(),
            storeAccesses: data?.storeAccesses?.map((elem) => ({
                value: elem.id,
                label: elem.name,
            })),
            isDemo: data.isDemo,
            preferences: data?.preferences ?? [],
        };
    };

    const handleCurrencyChange = (newValue) => {
        setInput((prev) => ({
            ...prev,
            defaultCurrency: {
                value: newValue?.value,
                label: currencies.find((c) => c.value === newValue?.value)
                    .label,
            },
        }));
    };

    const newFields = [
        {
            label: t("affiliate"),
            name: "name",
            type: FIELD_TYPES.Input,
            placeholder: t("affiliatePlaceholder"),
            required: true,
        },
        {
            label: t("maxUsers"),
            name: "maxUsersNumber",
            type: "number",
            min: "1",
            placeholder: t("maxUsersPlaceholder"),
            required: true,
        },
        {
            label: t("type"),
            name: "type",
            type: FIELD_TYPES.Dropdown,
            options: [
                {
                    value: "Enterprise",
                    label: t("enterprise"),
                    roles: [ROLES.superAdmin],
                },
                { value: "Lab", label: t("lab") },
                {
                    value: "Farm",
                    label: t("farm"),
                    roles: [ROLES.partnerAdmin, ROLES.support],
                },
                { value: "Consultant", label: t("consultant") },
            ],
            authorizeOptions: true,
            placeholder: t("selectPlaceholder", { entity: t("type") }),
            required: true,
        },
        {
            label: t("country"),
            name: "supportedCountryId",
            type: FIELD_TYPES.Dropdown,
            options: countries,
            placeholder: t("selectPlaceholder", { entity: t("country") }),
            required:
                isAuthorized([ROLES.superAdmin], roles) &&
                (!data || !data.partnerId),
            isClearable: true,
        },
        {
            label: t("endOfLicense"),
            name: "endOfLicense",
            type: "date",
            min: shortDateYMD(new Date()),
            required: true,
            role: !isAuthorized([ROLES.superAdmin], getRoles()),
        },
        {
            label: t("defaultCurrency"),
            name: "defaultCurrency",
            type: FIELD_TYPES.Dropdown,
            options: currencies,
            placeholder: t("selectPlaceholder", { entity: t("currency") }),
            role: !isAuthorized([ROLES.superAdmin], getRoles()),
            required: true,
            defaultValue: true,
            handler: handleCurrencyChange,
            disabled: data && !data?.isDefaultCurrencyEditable,
        },
        {
            label: t("abbreviation"),
            name: "abbreviation",
            type: FIELD_TYPES.Input,
            placeholder: t("abbreviationPlaceHolder"),
            isUpperCase: true,
        },
        {
            label: t("createBundleAccess"),
            name: "createBundleAccess",
            placeholder: t("selectPlaceholder", {
                entity: t("createBundleAccess"),
            }),
            type: FIELD_TYPES.Dropdown,
            options: [
                {
                    value: "true",
                    label: t("true"),
                },
                { value: "false", label: t("false") },
            ],
            role:
                !isAuthorized([ROLES.superAdmin], getRoles()) ||
                data?.partnerId,
        },
        {
            label: t("uploadModelAccess"),
            name: "uploadModelAccess",
            type: FIELD_TYPES.Dropdown,
            placeholder: t("selectPlaceholder", {
                entity: t("uploadModelAccess"),
            }),
            options: [
                {
                    value: "true",
                    label: t("true"),
                },
                { value: "false", label: t("false") },
            ],
            role:
                !isAuthorized([ROLES.superAdmin], getRoles()) ||
                data?.partnerId,
        },
        {
            label: t("couponAccess"),
            name: "createCouponAccess",
            type: FIELD_TYPES.Dropdown,
            placeholder: t("selectPlaceholder", {
                entity: t("couponAccess"),
            }),
            options: [
                {
                    value: "true",
                    label: t("true"),
                },
                { value: "false", label: t("false") },
            ],
            role:
                !isAuthorized([ROLES.superAdmin], getRoles()) ||
                data?.partnerId,
        },
        {
            label: t("externalId"),
            name: "externalId",
            type: FIELD_TYPES.Input,
            placeholder: t("externalIdPlaceholder"),
            required: false,
        },
        {
            label: t("materialSupplier"),
            name: "suppliersAccess",
            placeholder: t("selectPlaceholder", {
                entity: t("materialSupplier"),
            }),
            type: FIELD_TYPES.Dropdown,
            options: [
                {
                    value: "true",
                    label: t("true"),
                },
                { value: "false", label: t("false") },
            ],
            role:
                !isAuthorized([ROLES.superAdmin], getRoles()) ||
                data?.partnerId,
        },
        {
            label: t("labStoreAccess"),
            name: "storeAccesses",
            placeholder: t("selectPlaceholder", {
                entity: t("labStoreAccess"),
            }),
            type: FIELD_TYPES.Dropdown,
            options: labstoreAccesses,
            isMulti: true,
            defaultValue: true,
            role:
                !isAuthorized([ROLES.superAdmin], getRoles()) ||
                data?.partnerId,
        },
    ];

    const breadCrumbs = [
        {
            path: "/dashboard",
            element: () => <i className="fas fa-home" />,
        },
        {
            path: "/affiliates",
            element: () => <Trans i18nKey="affiliates">Affiliates</Trans>,
        },
        {
            element: () =>
                organization
                    ? t("buttonEditAffilate")
                    : t("buttonAddAffiliate"),
        },
    ];

    const getLabStoreAccessesOptions = async () => {
        const result = await LookupsAPI.getStoreAccesses();
        setLabstoreAccesses([
            ...result.map((access) => ({
                value: access.id,
                label: access.name,
            })),
        ]);
    };

    const handleSubmit = () => {
        setIsSubmitting(true);

        setIsFormSubmitted(true);
        if (!isFormValid) {
            setIsSubmitting(false);
            return;
        }

        let defaultCurrencyId = input.defaultCurrency?.value;
        let storeAccessIds = input?.storeAccesses?.map((elem) => elem.value);
        delete input.defaultCurrency;
        delete input.storeAccesses;

        if (organization) {
            let organizationData = {
                ...input,
                name: input.name?.trim(),
                createBundleAccess: input.createBundleAccess === "true",
                uploadModelAccess: input.uploadModelAccess === "true",
                suppliersAccess: input.suppliersAccess === "true",
                ...(input.abbreviation?.toUpperCase() !==
                    data.abbreviation?.toUpperCase() && {
                    abbreviation: input.abbreviation?.toUpperCase(),
                }),
                maxUsersNumber: parseInt(input.maxUsersNumber),
                endOfLicense: input.endOfLicense
                    ? new Date(input.endOfLicense).toISOString()
                    : null,
                defaultCurrencyId: defaultCurrencyId,
                createCouponAccess: input.createCouponAccess === "true",
                preferences:
                    input.preferences &&
                    input.preferences
                        .filter((preference) => preference.entity?.value)
                        .map((preference) => {
                            return {
                                ...preference,
                                entity: preference.entity?.value,
                            };
                        }),
            };

            organizationData = removeUnchangedProps(
                organization,
                organizationData,
                ["image"]
            );

            organizationData.id = organizationId;
            organizationData.storeAccessIds = storeAccessIds;
            OrganizationsAPI.updateOrganization(organizationData)
                .then((res) => {
                    if (res?.modified) {
                        LogCapture.info(
                            `Affiliate updated with name: ${input?.name} & id: ${organizationId}`
                        );
                        SuccessAlert(t("editAffiliateSuccessMessage"));
                        history.push("/affiliates");
                    }
                })
                .catch((retrievedError) => {
                    console.log(retrievedError);
                    LogCapture.error(
                        `Couldn't update affiliate with name: ${input?.name}`
                    );

                    ErrorAlert(t("editAffiliateFailMessage"));
                })
                .finally(() => setIsSubmitting(false));
        } else {
            let newOrg = {
                ...input,
                createBundleAccess: input.createBundleAccess === "true",
                uploadModelAccess: input.uploadModelAccess === "true",
                suppliersAccess: input.suppliersAccess === "true",
                name: input.name?.trim(),
                partnerId: partnerOrganization?.id,
                defaultCurrencyId: defaultCurrencyId || 1,
                createCouponAccess: input.createCouponAccess === "true",
                storeAccessIds,
                preferences:
                    input.preferences &&
                    input.preferences
                        .filter((preference) => preference.entity?.value)
                        .map((preference) => {
                            return {
                                ...preference,
                                entity: preference.entity?.value,
                            };
                        }),
            };
            newOrg.maxUsersNumber = parseInt(newOrg.maxUsersNumber);
            newOrg.endOfLicense =
                input.endOfLicense &&
                new Date(input.endOfLicense).toISOString();

            OrganizationsAPI.addNewOrganization(newOrg)
                .then((res) => {
                    if (res?.inserted) {
                        history.push("/affiliates");
                        SuccessAlert(t("addAffiliateSuccessMessage"));
                        LogCapture.info(
                            `Affiliate created with name: ${input?.name}`
                        );
                    }
                })
                .catch((retrievedError) => {
                    console.log(retrievedError);
                    LogCapture.error(
                        `Couldn't create affiliate with name: ${input?.name}, error: ${retrievedError}`
                    );
                    ErrorAlert(t("addAffiliateFailMessage"));
                })
                .finally(() => setIsSubmitting(false));
        }
    };

    const renderButton = () => {
        if (isSubmitting) {
            return (
                <>
                    <span className="animate-spin mr-3 inline-flex">
                        <i className="fas fa-sync"></i>
                    </span>
                    <Trans i18nKey="requesting">Requesting...</Trans>
                </>
            );
        } else {
            return (
                <>
                    {organization
                        ? t("buttonEditAffilate")
                        : t("buttonAddAffiliate")}
                </>
            );
        }
    };

    const handleCheckboxChange = (e) => {
        setInput((prevData) => ({ ...prevData, isDemo: e.target.checked }));
    };

    const reset = () => {
        const newInputObj = formResetHandler(newFields);
        setInput({
            ...newInputObj,
            defaultCurrency: input.defaultCurrency,
        });
    };

    return (
        <>
            <BreadCrumb crumbs={breadCrumbs} />
            <header className="main-customer-form-header main-content-header flex items-center">
                <i
                    className={`fas ${
                        organization ? "fa-user-edit" : "fa-user-plus"
                    }`}
                ></i>
                <h2 className="h2">
                    {organization
                        ? t("buttonEditAffilate")
                        : t("buttonAddAffiliate")}
                </h2>
            </header>
            <div className="card-table shadow">
                <Form
                    fields={newFields}
                    input={input}
                    setInput={setInput}
                    error={error}
                    isFormSubmitted={isFormSubmitted}
                    image="org-avatar"
                    hardReset={true}
                    hardResetFunction={reset}
                    inlineRender={
                        isAuthorized([ROLES.superAdmin], getRoles()) &&
                        !data?.partnerId
                            ? () => (
                                  <OrganizationPreferencesForm
                                      input={input}
                                      setInput={setInput}
                                      isFormSubmitted={isFormSubmitted}
                                      error={error}
                                  />
                              )
                            : null
                    }
                />
                <div
                    className={`main-customer-form-bnt flex items-center ${
                        isAuthorized([ROLES.superAdmin], roles)
                            ? "justify-between"
                            : "justify-end"
                    }`}
                >
                    {isAuthorized([ROLES.superAdmin], roles) &&
                        !data?.partnerId && (
                            <div className="checkbox-wrapper-input">
                                <input
                                    onChange={handleCheckboxChange}
                                    type="checkbox"
                                    className="checkbox-element"
                                    id="isDemo"
                                    name="isDemo"
                                    checked={input.isDemo || false}
                                />
                                <label htmlFor="isDemo">
                                    {t("organizationDemoModeMessage", {
                                        action: organization
                                            ? t("make")
                                            : t("create"),
                                    })}
                                </label>
                            </div>
                        )}
                    <div>
                        <Link to="/affiliates">
                            <button className="secondary-blue-btn ml-2">
                                <Trans i18nKey="cancel">Cancel</Trans>
                            </button>
                        </Link>
                        <button
                            onClick={handleSubmit}
                            className={`default-orange-outline-btn ml-2 ${
                                isSubmitting ? "disabled" : ""
                            }`}
                            disabled={isSubmitting}
                        >
                            {renderButton()}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default OrganizationForm;
